import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtINR, fmtMeters, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { AgingBar } from "../components/AgingBar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import { ArrowLeft, AlertTriangle, Download, FileText, Pencil, Phone, StickyNote } from "lucide-react";

const Pill = ({ status }) => {
  const map = {
    delivered: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    partial: "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]",
    pending: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
    paid: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    unpaid: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${map[status] || ""}`}>{status}</span>;
};

const Stat = ({ label, value, tone }) => (
  <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
    <span className="text-xs uppercase tracking-[0.1em] text-[#8CA3A7]">{label}</span>
    <div className={`font-mono text-xl tabular-nums mt-3 ${tone || "text-[#0A0D14]"}`}>{value}</div>
  </div>
);

export default function PartyDetail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const name = params.get("name") || "";
  const role = params.get("role") || "buyer";
  const [data, setData] = useState(null);

  const load = () => api.get(`/parties/detail`, { params: { name, role } })
    .then((r) => setData(r.data)).catch(() => setData({ orders: [] }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [name, role]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/parties/export", { params: { name, role }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${role}-${name}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const downloadStatement = async () => {
    try {
      const res = await api.get("/parties/statement", { params: { name, role }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${role}-${name}-statement.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate statement.");
    }
  };

  if (!data) return <div className="text-slate-400 font-mono text-sm">Loading party…</div>;

  const isBuyer = role === "buyer";

  return (
    <div className="space-y-6" data-testid="party-detail-page">
      <button onClick={() => navigate("/parties")} className="text-sm text-[#8CA3A7] flex items-center gap-1 hover:text-[#1C2331] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to parties
      </button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">{isBuyer ? "Buyer (Garment Exporter)" : "Seller (Weaver / Mill)"}</p>
          <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1" data-testid="party-detail-name">{name}</h1>
          {data.phone ? <p className="text-sm text-[#4B5563] mt-2 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#8CA3A7]" /> {data.phone}</p> : null}
          {data.notes ? <p className="text-sm text-[#4B5563] mt-1 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5 text-[#8CA3A7]" /> {data.notes}</p> : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="party-statement-pdf-btn" onClick={downloadStatement} className="text-sm bg-white border border-[#E6E4DD] rounded-sm px-3 py-2 font-medium flex items-center gap-1.5 hover:border-[#1C2331] transition-colors">
            <FileText className="w-3.5 h-3.5" /> PDF Statement
          </button>
          <button data-testid="party-export-csv-btn" onClick={exportCsv} className="text-sm bg-white border border-[#E6E4DD] rounded-sm px-3 py-2 font-medium flex items-center gap-1.5 hover:border-[#1C2331] transition-colors">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <EditPartyDialog name={name} role={role} notes={data.notes} phone={data.phone} onDone={(newName) => {
            if (newName && newName !== name) navigate(`/parties/detail?name=${encodeURIComponent(newName)}&role=${role}`);
            else load();
          }} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat label="Orders" value={data.order_count} />
        <Stat label="Total Value" value={fmtINR(data.total_value)} />
        <Stat label="Outstanding" value={fmtINR(data.outstanding)} tone={data.outstanding > 0 ? "text-[#D95D39]" : "text-[#065F46]"} />
        <Stat label="Overdue Orders" value={data.overdue_count} tone={data.overdue_count > 0 ? "text-[#991B1B]" : "text-[#0A0D14]"} />
        <Stat label="Delivered" value={fmtMeters(data.delivered_qty)} />
        <Stat label="Ordered Qty" value={fmtMeters(data.total_qty)} />
        <Stat label="Pending Deliveries" value={data.pending_deliveries} />
        <Stat label="Pending Payments" value={data.pending_payments} />
      </div>

      {data.aging && (
        <AgingBar aging={data.aging} />
      )}

      <div className="border border-[#E6E4DD] rounded-sm bg-white overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] bg-slate-50 border-b border-[#E6E4DD]">
              <th className="px-4 py-3">PO / Fabric</th>
              <th className="px-4 py-3">{isBuyer ? "Seller" : "Buyer"}</th>
              <th className="px-4 py-3 text-right">Dispatched / Qty</th>
              <th className="px-4 py-3 text-right">Value / Due</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr
                key={o.id}
                data-testid={`party-order-${o.id}`}
                onClick={() => navigate(`/orders/${o.id}`)}
                className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors cursor-pointer"
              >
                <td className="px-4 py-3">
                  <div className="font-mono text-[#0A0D14] flex items-center gap-1.5">
                    {o.po_number || "—"}
                    {o.overdue && <AlertTriangle className="w-3 h-3 text-[#991B1B]" />}
                  </div>
                  <div className="text-xs text-[#8CA3A7]">{o.fabric_type || "—"}</div>
                </td>
                <td className="px-4 py-3 text-[#0A0D14]">{isBuyer ? (o.seller_name || "—") : (o.buyer_name || "—")}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-[#4B5563]">
                  <div>{fmtMeters(o.delivered_quantity)}</div>
                  <div className="text-xs text-[#8CA3A7]">/ {fmtMeters(o.quantity_meters)}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-[#0A0D14]">
                  <div>{fmtINR(o.total_amount)}</div>
                  <div className="text-xs text-[#8CA3A7]">{fmtINR(o.outstanding_amount)} due{o.due_date ? ` · ${o.due_date}` : ""}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-start">
                    <Pill status={o.delivery_status} />
                    <Pill status={o.overdue ? "unpaid" : o.payment_status} />
                  </div>
                </td>
              </tr>
            ))}
            {data.orders.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">No orders for this party.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditPartyDialog({ name, role, notes, phone, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ new_name: name, notes: notes || "", phone: phone || "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm({ new_name: name, notes: notes || "", phone: phone || "" }); }, [name, notes, phone, open]);

  const save = async () => {
    setSaving(true);
    try {
      const trimmed = form.new_name.trim();
      if (trimmed && trimmed !== name) {
        await api.put("/parties/rename", { role, old_name: name, new_name: trimmed });
      }
      await api.put("/parties/meta", { name: trimmed || name, role, notes: form.notes, phone: form.phone });
      toast.success("Party updated.");
      setOpen(false);
      onDone(trimmed && trimmed !== name ? trimmed : null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="edit-party-btn" className="text-sm bg-[#1C2331] text-white rounded-sm px-3 py-2 font-medium flex items-center gap-1.5 hover:bg-[#2A3447] transition-colors">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit {role}</DialogTitle>
          <DialogDescription className="sr-only">Rename this party or add private notes and a phone number</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Name (renaming updates all their orders)</label>
            <input data-testid="edit-party-name" value={form.new_name} onChange={(e) => setForm({ ...form, new_name: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Phone (for WhatsApp / calls)</label>
            <input data-testid="edit-party-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Private notes</label>
            <textarea data-testid="edit-party-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
        </div>
        <button data-testid="save-party-edit-btn" onClick={save} disabled={saving} className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
